import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SkillModal = ({ skill, isOpen, onClose }) => {
  if (!isOpen || !skill) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-large">
        <div className="sticky top-0 bg-white border-b border-border p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-16 h-16 rounded-xl ${skill?.color} flex items-center justify-center`}>
                <Icon name={skill?.icon} size={32} color="white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-text-primary">{skill?.name}</h2>
                <p className="text-text-secondary">{skill?.category}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary"
            />
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Proficiency Overview */}
          <div className="bg-muted rounded-xl p-4">
            <h3 className="font-semibold text-text-primary mb-3">Proficiency Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{skill?.proficiency}</div>
                <div className="text-sm text-text-secondary">Level</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{skill?.experience}</div>
                <div className="text-sm text-text-secondary">Years</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{skill?.projects}</div>
                <div className="text-sm text-text-secondary">Projects</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{skill?.lastUsed}</div>
                <div className="text-sm text-text-secondary">Last Used</div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold text-text-primary mb-3">About This Technology</h3>
            <p className="text-text-secondary leading-relaxed">{skill?.description}</p>
          </div>

          {/* Notable Projects */}
          {skill?.notableProjects && skill?.notableProjects?.length > 0 && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Notable Projects</h3>
              <div className="space-y-3">
                {skill?.notableProjects?.map((project, index) => (
                  <div key={index} className="bg-muted rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-text-primary">{project?.name}</h4>
                        <p className="text-sm text-text-secondary mt-1">{project?.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-text-secondary">
                          <span>{project?.year}</span>
                          <span>•</span>
                          <span>{project?.role}</span>
                        </div>
                      </div>
                      {project?.impact && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-success">{project?.impact}</div>
                          <div className="text-xs text-text-secondary">Impact</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {skill?.certifications && skill?.certifications?.length > 0 && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Certifications & Achievements</h3>
              <div className="space-y-2">
                {skill?.certifications?.map((cert, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-success/5 rounded-lg border border-success/20">
                    <Icon name="Award" size={16} className="text-success" />
                    <div>
                      <div className="font-medium text-text-primary">{cert?.name}</div>
                      <div className="text-sm text-text-secondary">{cert?.issuer} • {cert?.year}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Learning Resources */}
          {skill?.learningResources && skill?.learningResources?.length > 0 && (
            <div>
              <h3 className="font-semibold text-text-primary mb-3">Learning Resources</h3>
              <div className="space-y-2">
                {skill?.learningResources?.map((resource, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-accent/5 rounded-lg border border-accent/20">
                    <Icon name="BookOpen" size={16} className="text-accent" />
                    <div>
                      <div className="font-medium text-text-primary">{resource?.title}</div>
                      <div className="text-sm text-text-secondary">{resource?.type} • {resource?.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current Learning Status */}
          {skill?.isLearning && (
            <div className="bg-accent/5 rounded-xl p-4 border border-accent/20">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="TrendingUp" size={20} className="text-accent" />
                <h3 className="font-semibold text-accent">Currently Learning</h3>
              </div>
              <p className="text-text-secondary mb-3">{skill?.learningGoal}</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 bg-accent rounded-full transition-all duration-500"
                  style={{ width: `${skill?.learningProgress}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center mt-2 text-sm">
                <span className="text-text-secondary">Progress</span>
                <span className="font-medium text-accent">{skill?.learningProgress}%</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillModal;